import { CanvasTexture, LinearFilter, LinearMipmapLinearFilter, SRGBColorSpace, Texture, TextureLoader } from 'three/webgpu';
import { FLOOR_THEMES, themeIndexForFloor, type FloorThemeDef } from '@src/game/arena/FloorThemes';

export type { FloorThemeDef };
export { FLOOR_THEMES, themeIndexForFloor, floorThemeDef, hexCss } from '@src/game/arena/FloorThemes';

/** Asset stems in floor order; see tools/gen-arenas.ts. */
export const FLOOR_THEME_ASSETS = FLOOR_THEMES.map((t) => t.asset);

export interface ArenaArt {
  /** One texture per theme, aligned with FLOOR_THEMES (painted PNG or canvas stand-in). */
  floors: Texture[];
  /** North/South arch with the portcullis lowered (front-facing top-down sprite). */
  gateClosed: Texture | null;
  /** Same N/S arch with no bars; drawn over gateClosed so the bars can rise behind the header. */
  gateFrame: Texture | null;
  /** East/West side-wall doorway with portcullis (upright 2.5D profile, not a rotated front arch). */
  gateSideClosed: Texture | null;
  /** Same E/W doorway with no bars. */
  gateSideFrame: Texture | null;
  torch: Texture | null;
  rune: Texture | null;
}

/** Theme index for a 1-based floor number. */
export function themeForFloor(floor: number): number {
  return themeIndexForFloor(floor);
}

/** @deprecated Use themeForFloor. Kept so older callers still compile. */
export function themeForTier(tier: number): number {
  return themeForFloor(tier);
}

function load(loader: TextureLoader, url: string, mips: boolean): Promise<Texture | null> {
  return new Promise((resolve) => {
    loader.load(
      url,
      (t) => {
        t.colorSpace = SRGBColorSpace;
        t.magFilter = LinearFilter;
        t.minFilter = mips ? LinearMipmapLinearFilter : LinearFilter;
        t.generateMipmaps = mips;
        t.needsUpdate = true;
        resolve(t);
      },
      undefined,
      () => resolve(null),
    );
  });
}

function hash(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function mixHex(a: number, b: number, t: number): string {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `rgb(${r},${g},${bl})`;
}

/** Distinctive stand-in floor if the painted PNG is missing. */
export function paintFloorTexture(theme: FloorThemeDef, index: number): Texture {
  const w = 1024;
  const h = 576;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d');
  if (g) paintThemeFloor(g, w, h, theme, index);
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.magFilter = LinearFilter;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

function paintThemeFloor(g: CanvasRenderingContext2D, w: number, h: number, theme: FloorThemeDef, index: number): void {
  g.fillStyle = hex(theme.surround);
  g.fillRect(0, 0, w, h);
  const inset = Math.round(w * 0.045);
  switch (index) {
    case 0:
      paintRome(g, w, h, inset, theme);
      break;
    case 1:
      paintEgyptian(g, w, h, inset, theme);
      break;
    case 2:
      paintAztec(g, w, h, inset, theme);
      break;
    case 3:
      paintVerdant(g, w, h, inset, theme);
      break;
    case 4:
      paintObsidian(g, w, h, inset, theme);
      break;
    default:
      paintInfernal(g, w, h, inset, theme);
      break;
  }
}

function paintRome(g: CanvasRenderingContext2D, w: number, h: number, inset: number, theme: FloorThemeDef): void {
  g.fillStyle = '#6a1c1c';
  g.fillRect(inset * 0.35, inset * 0.35, w - inset * 0.7, h - inset * 0.7);
  g.fillStyle = hex(theme.trim);
  g.fillRect(inset * 0.7, inset * 0.7, w - inset * 1.4, h - inset * 1.4);
  const x0 = inset;
  const y0 = inset;
  const iw = w - inset * 2;
  const ih = h - inset * 2;
  const cols = 8;
  const rows = 5;
  const tw = iw / cols;
  const th = ih / rows;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const n = hash(x * 19 + y * 47);
      g.fillStyle = mixHex(0xe8dcc4, 0xb8a888, n);
      g.fillRect(x0 + x * tw + 2, y0 + y * th + 2, tw - 4, th - 4);
    }
  }
  g.strokeStyle = 'rgba(180,180,190,0.35)';
  g.lineWidth = 1.2;
  for (let i = 0; i < 40; i++) {
    const sx = x0 + hash(i * 3.1) * iw;
    const sy = y0 + hash(i * 5.7) * ih;
    g.beginPath();
    g.moveTo(sx, sy);
    g.bezierCurveTo(sx + 40, sy + hash(i) * 30 - 15, sx + 80, sy + hash(i + 2) * 40 - 20, sx + 120, sy + hash(i + 4) * 20 - 10);
    g.stroke();
  }
  paintRing(g, w * 0.5, h * 0.5, Math.min(iw, ih) * 0.28, theme.trim, 0x6a1c1c);
}

function paintEgyptian(g: CanvasRenderingContext2D, w: number, h: number, inset: number, theme: FloorThemeDef): void {
  g.fillStyle = '#1a3a7a';
  g.fillRect(inset * 0.3, inset * 0.3, w - inset * 0.6, h - inset * 0.6);
  g.fillStyle = hex(theme.trim);
  g.fillRect(inset * 0.65, inset * 0.65, w - inset * 1.3, h - inset * 1.3);
  const x0 = inset;
  const y0 = inset;
  const iw = w - inset * 2;
  const ih = h - inset * 2;
  const rows = 12;
  const th = ih / rows;
  for (let y = 0; y < rows; y++) {
    const odd = y & 1;
    const cols = odd ? 15 : 14;
    const tw = iw / cols;
    const ox = odd ? -tw * 0.5 : 0;
    for (let x = -1; x <= cols; x++) {
      const n = hash(x * 13 + y * 29);
      g.fillStyle = mixHex(0xd4b070, 0xa87838, n);
      g.fillRect(x0 + ox + x * tw + 1, y0 + y * th + 1, tw - 2, th - 2);
    }
  }
  g.fillStyle = hex(theme.rune);
  g.fillRect(x0, y0 + ih * 0.18, iw, 6);
  g.fillRect(x0, y0 + ih * 0.82 - 6, iw, 6);
  const cx = w * 0.5;
  const cy = h * 0.5;
  g.fillStyle = hex(theme.trim);
  g.beginPath();
  g.arc(cx, cy, ih * 0.16, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = hex(theme.rune);
  g.beginPath();
  g.ellipse(cx, cy, ih * 0.07, ih * 0.11, 0, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = hex(theme.trim);
  g.fillRect(cx - iw * 0.22, cy - 5, iw * 0.16, 10);
  g.fillRect(cx + iw * 0.06, cy - 5, iw * 0.16, 10);
}

function paintAztec(g: CanvasRenderingContext2D, w: number, h: number, inset: number, theme: FloorThemeDef): void {
  g.fillStyle = '#2a1810';
  g.fillRect(inset * 0.3, inset * 0.3, w - inset * 0.6, h - inset * 0.6);
  g.fillStyle = hex(theme.trim);
  g.fillRect(inset * 0.7, inset * 0.7, w - inset * 1.4, h - inset * 1.4);
  const x0 = inset;
  const y0 = inset;
  const iw = w - inset * 2;
  const ih = h - inset * 2;
  g.fillStyle = '#8a3a1a';
  g.fillRect(x0, y0, iw, ih);
  const step = 36;
  for (let y = y0 - step; y < y0 + ih + step; y += step) {
    for (let x = x0 - step; x < x0 + iw + step; x += step) {
      const n = hash(x * 0.1 + y * 0.17);
      g.fillStyle = mixHex(0x6a2810, 0xa84820, n);
      g.beginPath();
      g.moveTo(x, y + step * 0.5);
      g.lineTo(x + step * 0.5, y);
      g.lineTo(x + step, y + step * 0.5);
      g.lineTo(x + step * 0.5, y + step);
      g.closePath();
      g.fill();
      if (n > 0.72) {
        g.fillStyle = hex(theme.trim);
        g.beginPath();
        g.arc(x + step * 0.5, y + step * 0.5, 4, 0, Math.PI * 2);
        g.fill();
      }
    }
  }
  const cx = w * 0.5;
  const cy = h * 0.5;
  const r = Math.min(iw, ih) * 0.22;
  g.fillStyle = hex(theme.trim);
  paintSteppedDiamond(g, cx, cy, r * 1.15);
  g.fillStyle = hex(theme.rune);
  g.beginPath();
  g.arc(cx, cy, r * 0.55, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = hex(theme.torch);
  g.beginPath();
  g.arc(cx, cy, r * 0.28, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = hex(theme.torch);
  g.lineWidth = 4;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    g.beginPath();
    g.moveTo(cx + Math.cos(a) * r * 0.7, cy + Math.sin(a) * r * 0.7);
    g.lineTo(cx + Math.cos(a) * r * 1.05, cy + Math.sin(a) * r * 1.05);
    g.stroke();
  }
}

function paintVerdant(g: CanvasRenderingContext2D, w: number, h: number, inset: number, theme: FloorThemeDef): void {
  g.fillStyle = '#0c1410';
  g.fillRect(inset * 0.3, inset * 0.3, w - inset * 0.6, h - inset * 0.6);
  const x0 = inset;
  const y0 = inset;
  const iw = w - inset * 2;
  const ih = h - inset * 2;
  const cols = 14;
  const rows = 9;
  const tw = iw / cols;
  const th = ih / rows;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const n = hash(x * 11 + y * 23);
      g.fillStyle = mixHex(0x2a3a28, 0x1a281c, n);
      g.fillRect(x0 + x * tw + 1, y0 + y * th + 1, tw - 2, th - 2);
      if (n > 0.55) {
        g.fillStyle = `rgba(40,160,90,${0.15 + n * 0.25})`;
        g.beginPath();
        g.arc(x0 + (x + 0.5) * tw, y0 + (y + 0.5) * th, 6 + n * 8, 0, Math.PI * 2);
        g.fill();
      }
    }
  }
  paintRing(g, w * 0.5, h * 0.5, Math.min(iw, ih) * 0.26, theme.rune, theme.trim);
}

function paintObsidian(g: CanvasRenderingContext2D, w: number, h: number, inset: number, theme: FloorThemeDef): void {
  g.fillStyle = '#0a0b12';
  g.fillRect(inset * 0.3, inset * 0.3, w - inset * 0.6, h - inset * 0.6);
  const x0 = inset;
  const y0 = inset;
  const iw = w - inset * 2;
  const ih = h - inset * 2;
  const cols = 16;
  const rows = 10;
  const tw = iw / cols;
  const th = ih / rows;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const n = hash(x * 7 + y * 17);
      g.fillStyle = mixHex(0x12141c, 0x1c1828, n);
      g.fillRect(x0 + x * tw + 1, y0 + y * th + 1, tw - 2, th - 2);
    }
  }
  g.strokeStyle = 'rgba(154,92,255,0.35)';
  g.lineWidth = 1.5;
  for (let x = 0; x <= cols; x++) {
    g.beginPath();
    g.moveTo(x0 + x * tw, y0);
    g.lineTo(x0 + x * tw, y0 + ih);
    g.stroke();
  }
  for (let y = 0; y <= rows; y++) {
    g.beginPath();
    g.moveTo(x0, y0 + y * th);
    g.lineTo(x0 + iw, y0 + y * th);
    g.stroke();
  }
  paintRing(g, w * 0.5, h * 0.5, Math.min(iw, ih) * 0.24, theme.rune, theme.trim);
}

function paintInfernal(g: CanvasRenderingContext2D, w: number, h: number, inset: number, theme: FloorThemeDef): void {
  g.fillStyle = '#1a0804';
  g.fillRect(inset * 0.3, inset * 0.3, w - inset * 0.6, h - inset * 0.6);
  const x0 = inset;
  const y0 = inset;
  const iw = w - inset * 2;
  const ih = h - inset * 2;
  const cols = 10;
  const rows = 6;
  const tw = iw / cols;
  const th = ih / rows;
  g.fillStyle = hex(theme.torch);
  g.fillRect(x0, y0, iw, ih);
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const n = hash(x * 9 + y * 21);
      g.fillStyle = mixHex(0x1a1210, 0x2a1814, n);
      g.fillRect(x0 + x * tw + 3, y0 + y * th + 3, tw - 6, th - 6);
    }
  }
  paintRing(g, w * 0.5, h * 0.5, Math.min(iw, ih) * 0.25, theme.rune, theme.torch);
}

function paintRing(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, outer: number, inner: number): void {
  g.strokeStyle = hex(outer);
  g.lineWidth = 5;
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.stroke();
  g.strokeStyle = hex(inner);
  g.lineWidth = 2;
  g.beginPath();
  g.arc(cx, cy, r * 0.72, 0, Math.PI * 2);
  g.stroke();
  g.beginPath();
  g.arc(cx, cy, r * 0.4, 0, Math.PI * 2);
  g.stroke();
}

function paintSteppedDiamond(g: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  g.beginPath();
  g.moveTo(cx, cy - r);
  g.lineTo(cx + r * 0.35, cy - r * 0.35);
  g.lineTo(cx + r, cy);
  g.lineTo(cx + r * 0.35, cy + r * 0.35);
  g.lineTo(cx, cy + r);
  g.lineTo(cx - r * 0.35, cy + r * 0.35);
  g.lineTo(cx - r, cy);
  g.lineTo(cx - r * 0.35, cy - r * 0.35);
  g.closePath();
  g.fill();
}

/** Loads generated arena textures; canvas floors stand in when a PNG is missing. */
export async function loadArenaArt(basePath = '/assets/arena'): Promise<ArenaArt | null> {
  const loader = new TextureLoader();
  const [loaded, gateClosed, gateFrame, gateSideClosed, gateSideFrame, torch, rune] = await Promise.all([
    Promise.all(FLOOR_THEME_ASSETS.map((n) => load(loader, `${basePath}/${n}.png`, true))),
    load(loader, `${basePath}/gate_closed.png`, false),
    load(loader, `${basePath}/gate_frame.png`, false),
    load(loader, `${basePath}/gate_side_closed.png`, false),
    load(loader, `${basePath}/gate_side_frame.png`, false),
    load(loader, `${basePath}/torch.png`, false),
    load(loader, `${basePath}/rune_mask.png`, false),
  ]);
  const floors = loaded.map((t, i) => t ?? paintFloorTexture(FLOOR_THEMES[i], i));
  return { floors, gateClosed, gateFrame, gateSideClosed, gateSideFrame, torch, rune };
}
